import { useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import { useFilters } from "../../../../Components/Filters/Filters.hook";
import { QUERY_AI_CITATIONS } from "../../../../GQL/CitationsQuery";
import { canonicalAsPath } from "../../../../Utils/canonicalAsPath";

export type CitationMetadata = {
	uuid: string;
	location_title: string;
	location_breadcrumb: string;
	offer_type_title: string;
	category_title?: string;
	bedroom_id?: string;
	title: string;
	url: string;
	location_level: number;
};

export type CitationData = {
	html_description?: string;
	metadata?: CitationMetadata;
};

export const hasStructuredCitationFormat = (html?: string) => {
	if (!html) {
		return false;
	}

	return /<h3\b[^>]*>\s*<strong>[\s\S]*?<\/strong>\s*<\/h3>\s*<p>/i.test(html);
};

const hasActiveExcludedFilter = (value: any) => {
	if (value === undefined || value === null) {
		return false;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (typeof value === "boolean") {
		return value;
	}

	if (typeof value === "string") {
		return value.trim().length > 0;
	}

	if (typeof value === "number") {
		return value > 0;
	}

	return true;
};

const toSlug = (value?: string) => {
	if (!value) {
		return undefined;
	}

	return value
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "-");
};

const getTagText = (filterTag: any) => {
	if (!filterTag) {
		return undefined;
	}

	if (typeof filterTag === "string") {
		return filterTag;
	}

	return filterTag.text || filterTag.name || filterTag?.value?.name;
};

const normalizeBedrooms = (bedrooms: any) => {
	if (bedrooms === undefined || bedrooms === null) {
		return undefined;
	}

	const normalizedBedrooms = (Array.isArray(bedrooms) ? bedrooms : [bedrooms])
		.map((bedroom: any) => Number(bedroom))
		.filter((bedroom: number) => !Number.isNaN(bedroom));

	return normalizedBedrooms.length > 0 ? normalizedBedrooms : undefined;
};

const useCitationsResults = () => {
	const { filters, filtersTags } = useFilters();
	const { asPath } = useRouter();

	// Transform filters to match the citation query structure
	const getCitationParams = () => {
		// Only execute if we have some basic filters AND we're on page 1
		if (!filters?.operation_type_id && !filters?.property_type_id) {
			return null;
		}

		// Only show citations on page 1
		const currentPage = filters?.page?.value || filters?.page || 1;
		if (currentPage !== 1) {
			return null;
		}

		// Return null if filters contains any of the specified keys
		const excludedKeys = [
			"publicationDate",
			"searchstring",
			"seaview",
			"owner_id",
			"projects",
			"projectTitle",
			"withBase",
			"auctionType",
			"search_by_point_of_interest",
			"overID",
			"privateOwner",
			"facilitiesGroup",
			"security",
			"socialHousing",
			"garage",
			"hasGarage",
			"constStatesID",
			"floors",
			"dispositionID",
			"condominium",
			"minPrice",
			"maxPrice",
			"m2Min",
			"m2Max",
			"commonExpenses",
			"groundUssageID",
			"season",
			"dateFrom",
			"dateTo",
			"guests",
			"bathrooms",
			"seaDistanceID",
			"map_bounds",
			"map_polygon",
			"map_polygons",
			"securityId",
			"percentage_sold",
			"percentage_finished",
			"sponsor",
			"architect",
			"builder",
			"minM2Price",
			"maxM2Price",
			"tour3d",
			"minOccupationDate",
			"maxOccupationDate",
			"seen",
			"stratum",
			"antiquity",
		];

		for (const key of excludedKeys) {
			if (filters && hasActiveExcludedFilter(filters[key])) {
				return null;
			}
		}

		const params: any = {};

		try {
			// Sólo el path: ni query-string ni hash (INF-263). El por qué está en
			// `canonicalAsPath`. El backend resuelve la citation por location + operation e ignora
			// el query-string (verificado: misma citation para `?utm_source`, `?basura=zzz` y sin
			// query), así que mandar el path no cambia la respuesta.
			params.url = canonicalAsPath(asPath);

			const neighborhoodTag =
				Array.isArray(filtersTags?.neighborhood_id) &&
				filtersTags.neighborhood_id.length === 1
					? filtersTags.neighborhood_id[0]
					: null;
			const estateTag = filtersTags?.estate_id;
			const primaryLocation = Array.isArray(filters?.locations)
				? filters.locations[0]
				: filters?.locations;

			const locationTitle =
				getTagText(neighborhoodTag) || getTagText(estateTag) || primaryLocation?.name;

			// Map operation type
			if (filters.operation_type_id) {
				params.operation_type_id = filters.operation_type_id;
			}

			// Map property type
			if (filters.property_type_id) {
				if (Array.isArray(filters.property_type_id)) {
					params.property_type_id = filters.property_type_id.map(
						(pt: any) => pt?.value || pt
					);
				} else {
					params.property_type_id = [
						(filters.property_type_id as any)?.value || filters.property_type_id,
					];
				}
			}

			// Map bedrooms
			const bedrooms = normalizeBedrooms(filters.bedrooms);
			if (bedrooms) {
				params.bedrooms = bedrooms;
			}

			// Map locations - clean up to only include fields accepted by LocationInput
			if (filters.locations) {
				const cleanLocations = (Array.isArray(filters.locations)
					? filters.locations
					: [filters.locations]
				).map((location: any) => ({
					id: location.id,
					type: location.type,
					// Remove name, estate and other fields not accepted by LocationInput
				}));

				if (cleanLocations.length > 0) {
					params.locations = cleanLocations;
				}
			}

			if (locationTitle) {
				params.location_title = locationTitle;
				params.location_slug = toSlug(locationTitle);
			}

			// Also add to locations array if not already there
			if (neighborhoodTag && !params.locations && neighborhoodTag?.value) {
				params.locations = [
					{
						id: neighborhoodTag.value,
						type: "NEIGHBOURHOOD", // or NEIGHBORHOOD based on backend expectation
					},
				];
			}

			return Object.fromEntries(
				Object.entries(params).filter(([_, value]) => {
					if (value === undefined || value === null) {
						return false;
					}

					if (Array.isArray(value)) {
						return value.length > 0;
					}

					return true;
				})
			);
		} catch (error) {
			return null;
		}
	};

	const citationParams = getCitationParams();

	const { data, loading, error } = useQuery(QUERY_AI_CITATIONS, {
		variables: citationParams || {},
		skip: !citationParams || Object.keys(citationParams || {}).length === 0,
		fetchPolicy: "cache-first",
		notifyOnNetworkStatusChange: true,
		errorPolicy: "all", // Continue even if there are GraphQL errors
	});

	return {
		loading,
		data: data?.aiCitationSearch as CitationData,
		error,
		hasCitations: !!data?.aiCitationSearch?.html_description,
		hasStructuredFormat: hasStructuredCitationFormat(data?.aiCitationSearch?.html_description),
	};
};

export { useCitationsResults };

